import noImage from "assets/images/no-image.jpg";
import { Fragment, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import useOrderQuery from "components/order/useOrderQuery";

const Order = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const { orders, pagination, loading, error } = useOrderQuery({
    page: currentPage,
  });

  // Handle image error (unused but kept for potential future use)
  const onError = useCallback((e) => {
    e.target.src = noImage;
  }, []);

  // Convert numeric status to string
  const getStatusText = useCallback((status) => {
    switch (status) {
      case 1:
        return "pending";
      case 2:
        return "processing";
      case 3:
        return "shipped";
      case 4:
        return "completed";
      case 5:
        return "cancelled";
      default:
        return "unknown";
    }
  }, []);

  // Determine status badge color
  const getStatusColor = useCallback((status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "processing":
        return "info";
      case "shipped":
        return "primary";
      case "cancelled":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  }, []);

  // Pagination handler
  const goToPage = useCallback(
    (page) => {
      if (
        page >= 1 &&
        page <= (pagination?.lastPage || 1) &&
        page !== currentPage
      ) {
        setCurrentPage(page);
      }
    },
    [pagination?.lastPage, currentPage]
  );

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading orders: {error.message}
      </div>
    );
  }

  // Empty state
  if (!Array.isArray(orders) || orders.length === 0) {
    return (
      <div className="text-center py-5 empty-state">
        <h4>No orders found</h4>
        <Link to="/" className="btn btn-dark mt-3">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid order-container">
        <div className="account-order">
          <div className="order-grid-container">
            <div className="order-grid row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
              {orders.map((order) => (
                <div key={order.id} className="col">
                  <Link
                    to={`/orders-details/${order.id}`}
                    className="text-decoration-none order-link"
                  >
                    <div className="order-card">
                      <div className="order-card-header">
                        <h5>#{order.tracking_number || order.id}</h5>
                        <span
                          className={`badge bg-${getStatusColor(
                            getStatusText(order.status)
                          )}`}
                        >
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <div className="order-card-body">
                        <div className="order-detail">
                          <span className="detail-label">Date:</span>
                          <span>{new Date(order.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="order-detail">
                          <span className="detail-label">Total:</span>
                          <strong>₹{parseFloat(order.total).toFixed(2)}</strong>
                        </div>
                        <div className="order-detail">
                          <span className="detail-label">Payment:</span>
                          <span>{order.payment_status}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Pagination Controls */}
        {pagination?.lastPage > 1 && (
          <nav aria-label="Order pagination" className="mt-4">
            <ul className="pagination justify-content-center">
              <li
                className={`page-item ${
                  pagination.currentPage === 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => goToPage(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {Array.from(
                { length: pagination.lastPage },
                (_, i) => i + 1
              ).map((page) => (
                <li
                  key={page}
                  className={`page-item ${
                    pagination.currentPage === page ? "active" : ""
                  }`}
                >
                  <button className="page-link" onClick={() => goToPage(page)}>
                    {page}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  pagination.currentPage === pagination.lastPage
                    ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => goToPage(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.lastPage}
                >
                  Next
                </button>
              </li>
            </ul>
            <div className="text-center text-muted pagination-info">
              Showing{" "}
              {(pagination.currentPage - 1) * pagination.perPage + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.perPage,
                pagination.total
              )}{" "}
              of {pagination.total} orders
            </div>
          </nav>
        )}
      </div>
    </Fragment>
  );
};

export default Order;